import React, { useCallback, useEffect, useState, useRef } from "react";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_business_ownership_type_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "organization_business_ownership_type_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};


function BusinessOwnershipTypeList() {
  const [ownershipTypes, setOwnershipTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const defaultVisibleColumns=["organization_business_ownership_type_name","business_ownership_type_category"]; 
  const mandatoryColumns=["organization_business_ownership_type_name","business_ownership_type_category"];  

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_business_ownership_type_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/business-ownership-types-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const b = data.map((item) => {
          return {
            ...item,
            id: item.organization_business_ownership_type_id,
            business_ownership_type_category: item.business_ownership_type_category_name || item.business_ownership_type_category || '',
            business_ownership_type_category_name: item.business_ownership_type_category_name || item.business_ownership_type_category || '',
          };
        });

        setOwnershipTypes(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching business ownership types:", error);
        toast.error("Failed to load business ownership types");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/businessownershiptype/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Business Ownership Type deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Business Ownership Type";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete Business Ownership Type");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );



  const handleEdit = useCallback(

    (item) => {

         navigate(`/organization-configration/business-ownership-type/edit/${item.id}`);

    },

    [navigate]

  );



                 const handleShow = useCallback(
    (item) => {
      navigate(`/organization-configration/business-ownership-type/view/${item.id}`)
    },
    [navigate],
  )



  return (
    <>
      <Layout4
        loading={loading}
        add_action={"BUSINESS_OWNERSHIP_TYPE_ADD"}
        heading={"Business Ownership Type"}
        btnName={"Add Business Ownership Type"}
        Data={ownershipTypes}
        delete_action={"BUSINESS_OWNERSHIP_TYPE_DELETE"}
        tableHeaders={[
          {
            name: "Business Ownership Type Name",
            value_key: "organization_business_ownership_type_name",
            textStyle: "capitalize",
          },
          {
            name: "Ownership Type Category",
            value_key: "business_ownership_type_category_name",
            textStyle: "capitalize",
          },
        ]}
        Icons={[
          <ExitToAppIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <ViewHeadlineIcon color="primary" />,
          <ExitToAppIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Business Ownership Type",
          "Business Ownership Type",
          "Add Business Ownership Type",
          "Business Ownership Type",
        ]}
        Route={"/organization-configration/business-ownership-type"}
        setData={setOwnershipTypes}
        DeleteFunc={handleDelete}
      />

   

      <TableDataGeneric
        tableName="organization_business_ownership_types"
        primaryKey="organization_business_ownership_type_id"
        heading="Business Ownership Types"
        data={ownershipTypes}
        showActions={true}
        sortname="organization_business_ownership_type_name"
        Route="/organization-configration/business-ownership-type"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        handleShow={handleShow}
        token={localStorage.getItem("token")}
        organizationUserId={userData?.organization_user_id}
        showLayoutButtons={true}
        edit_delete_action={["BUSINESS_OWNERSHIP_TYPE_DELETE", "BUSINESS_OWNERSHIP_TYPE_EDIT"]}
        paginationData={paginationData}
        filterModule="organization_business_ownership_types"
        config={{
          defaultVisibleColumns: defaultVisibleColumns,
          mandatoryColumns: mandatoryColumns
        }}
      />
 
    </>
  );
}

export default BusinessOwnershipTypeList;
